/**
 * Console transport for the Logger package.
 *
 * Renders richly-styled, grouped console output with colour-coded badges,
 * timestamps, namespace labels, stack traces, breadcrumb trails, timers,
 * and tabular data.
 * @module ConsoleTransport
 */

import {
  LEVEL_EMOJIS,
  LEVEL_NAMES,
  LogLevel,
  type LogEntry,
  type Transport,
} from "../types";

const BADGE =
  "padding:2px 6px;border-radius:3px;font-weight:600;font-size:11px;";

const LEVEL_BADGE_STYLES: Record<LogLevel, string> = {
  [LogLevel.DEBUG]: `background:#64748B;color:#fff;${BADGE}`,
  [LogLevel.INFO]: `background:#3B82F6;color:#fff;${BADGE}`,
  [LogLevel.WARN]: `background:#D97706;color:#fff;${BADGE}font-weight:700;`,
  [LogLevel.ERROR]: `background:#EF4444;color:#fff;${BADGE}font-weight:700;`,
  [LogLevel.SILENT]: "",
};

const STACK_ICONS: Record<LogLevel, string> = {
  [LogLevel.DEBUG]: "🔻",
  [LogLevel.INFO]: "🔽",
  [LogLevel.WARN]: "🔶",
  [LogLevel.ERROR]: "❌",
  [LogLevel.SILENT]: "",
};

const STACK_STYLES: Record<LogLevel, string> = {
  [LogLevel.DEBUG]: "color:#64748B;font-size:11px;",
  [LogLevel.INFO]: "color:#3B82F6;font-size:11px;",
  [LogLevel.WARN]: "color:#F59E0B;font-size:11px;",
  [LogLevel.ERROR]: "color:#EF4444;font-size:11px;",
  [LogLevel.SILENT]: "",
};

const TIMESTAMP_BADGE = `background:#475569;color:#E2E8F0;${BADGE}`;
const NAMESPACE_BADGE = `background:#8B5CF6;color:#fff;${BADGE}`;
const FILE_BADGE = `background:#06B6D4;color:#fff;${BADGE}font-style:italic;`;
const TIMER_BADGE = `background:#10B981;color:#fff;${BADGE}`;
const TABLE_BADGE = `background:#8B5CF6;color:#fff;${BADGE}font-weight:700;`;
const MSG_STYLE = "color:inherit;font-weight:normal;font-style:normal;";
const BREADCRUMB_HEADER_STYLE = "color:#A78BFA;font-weight:bold;padding:2px 0;";
const BREADCRUMB_STYLE = "color:#94A3B8;font-style:italic;padding-left:8px;";

function formatTimestamp(date: Date): string {
  const h = String(date.getHours()).padStart(2, "0");
  const m = String(date.getMinutes()).padStart(2, "0");
  const s = String(date.getSeconds()).padStart(2, "0");
  const ms = String(date.getMilliseconds()).padStart(3, "0");
  return `${h}:${m}:${s}.${ms}`;
}

function formatFileLabel(fileName?: string): string | null {
  return fileName ?? null;
}

/**
 * Transport that writes structured log entries to the browser console
 * with coloured badges, expandable groups, stack traces, and breadcrumb trails.
 */
export class ConsoleTransport implements Transport {
  /**
   * Renders a full log entry as a console group with badges, message, optional data/error,
   * a collapsed stack trace, and breadcrumbs (for ERROR level).
   *
   * @param entry - The log entry to render.
   */
  log(entry: LogEntry): void {
    const badgeStyle = LEVEL_BADGE_STYLES[entry.level];
    if (!badgeStyle && entry.level !== LogLevel.SILENT) return;

    const emoji = LEVEL_EMOJIS[entry.level];
    const levelName = LEVEL_NAMES[entry.level];
    const time = formatTimestamp(entry.timestamp);
    const fileLabel = formatFileLabel(entry.fileName);
    const stackIcon = STACK_ICONS[entry.level];
    const stackStyle = STACK_STYLES[entry.level];

    // Build the header line (badges only)
    const parts: string[] = [];
    const styles: string[] = [];

    parts.push(`${emoji} %c${time}`);
    styles.push(TIMESTAMP_BADGE);

    parts.push(` %c${levelName}`);
    styles.push(badgeStyle);

    parts.push(` %c${entry.namespace}`);
    styles.push(NAMESPACE_BADGE);

    if (fileLabel) {
      parts.push(` %c${fileLabel}`);
      styles.push(FILE_BADGE);
    }

    const formatStr = parts.join("");

    // Open group (expanded by default)
    console.group(formatStr, ...styles);

    if (entry.data !== undefined) {
      console.log(`%c${entry.message}`, MSG_STYLE, entry.data);
    } else {
      console.log(`%c${entry.message}`, MSG_STYLE);
    }

    if (entry.error) {
      console.log(`%c${stackIcon} Error:`, stackStyle, entry.error);
    }

    // Stack trace (collapsed by default)
    console.groupCollapsed(`%c${stackIcon} Stack Trace`, stackStyle);
    console.trace();
    console.groupEnd();

    // Breadcrumbs for errors (collapsed by default)
    if (
      entry.level === LogLevel.ERROR &&
      entry.breadcrumbs &&
      entry.breadcrumbs.length > 0
    ) {
      this.printBreadcrumbs(entry.breadcrumbs);
    }

    console.groupEnd();
  }

  /**
   * Outputs an array of objects as a console table.
   *
   * @param data - Row data to display.
   * @param columns - Optional column subset to show.
   */
  table(data: unknown[], columns?: string[]): void {
    console.log("%c📊 Table Output", TABLE_BADGE);
    if (columns) {
      console.table(data, columns);
    } else {
      console.table(data);
    }
  }

  /**
   * Renders a timer result as a styled console group showing elapsed milliseconds.
   *
   * @param namespace - Logger namespace for the badge.
   * @param fileName - Optional file name for the badge.
   * @param label - Timer label.
   * @param elapsedMs - Elapsed time in milliseconds.
   */
  logTimer(
    namespace: string,
    fileName: string | undefined,
    label: string,
    elapsedMs: number,
  ): void {
    const time = formatTimestamp(new Date());
    const fileLabel = formatFileLabel(fileName);

    const parts: string[] = [];
    const styles: string[] = [];

    parts.push(`⏱️ %c${time}`);
    styles.push(TIMESTAMP_BADGE);

    parts.push(` %cTIMER`);
    styles.push(TIMER_BADGE);

    parts.push(` %c${namespace}`);
    styles.push(NAMESPACE_BADGE);

    if (fileLabel) {
      parts.push(` %c${fileLabel}`);
      styles.push(FILE_BADGE);
    }

    const formatStr = parts.join("");

    console.group(formatStr, ...styles);
    console.log(`%c${label}: ${elapsedMs.toFixed(2)}ms`, MSG_STYLE);
    console.groupEnd();
  }

  private printBreadcrumbs(breadcrumbs: import("../types").Breadcrumb[]): void {
    console.groupCollapsed(
      "%c🧭 Breadcrumb Trail (%d events)",
      BREADCRUMB_HEADER_STYLE,
      breadcrumbs.length,
    );

    for (const crumb of breadcrumbs) {
      const time = formatTimestamp(crumb.timestamp);
      const emoji = LEVEL_EMOJIS[crumb.level];
      const level = LEVEL_NAMES[crumb.level];
      const file = crumb.fileName ? ` [${crumb.fileName}]` : "";
      console.log(
        `%c${time} ${emoji} ${level} [${crumb.namespace}]${file} ${crumb.message}`,
        BREADCRUMB_STYLE,
      );
    }

    console.groupEnd();
  }
}
